// Which date groups the user has collapsed in a fax list.
//
// Persisted, deliberately: the inbox refetches on a timer and after every
// match/assign, so state held only in memory would spring back open under the
// user mid-scroll. Keyed per list, so collapsing a date in the inbox does not
// also collapse it in Spam.
import { useCallback, useState } from "react";

const keyFor = (listId) => `vmai:fax-collapsed-dates:${listId}`;

/** The stored set for a list, tolerating absent or corrupt storage. */
export function readCollapsed(listId, storage = safeStorage()) {
  try {
    const raw = storage?.getItem(keyFor(listId));
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch (_) {
    return new Set();
  }
}

function safeStorage() {
  try {
    return typeof window !== "undefined" ? window.localStorage : null;
  } catch (_) {
    return null; // Storage blocked (private mode / embedded contexts).
  }
}

/**
 * @param listId distinguishes one fax list from another ("inbox", "spam").
 * @returns { isCollapsed, toggle, collapseAll, expandAll }
 */
export function useCollapsedDates(listId) {
  const [collapsed, setCollapsed] = useState(() => readCollapsed(listId));

  const persist = useCallback((next) => {
    const storage = safeStorage();
    try {
      storage?.setItem(keyFor(listId), JSON.stringify([...next]));
    } catch (_) { /* nothing to do; the UI still works for this session */ }
    return next;
  }, [listId]);

  const toggle = useCallback((dateKey) => {
    setCollapsed((prev) => {
      const next = new Set(prev);
      if (next.has(dateKey)) next.delete(dateKey);
      else next.add(dateKey);
      return persist(next);
    });
  }, [persist]);

  const collapseAll = useCallback((dateKeys) => {
    setCollapsed(() => persist(new Set(dateKeys || [])));
  }, [persist]);

  const expandAll = useCallback(() => {
    setCollapsed(() => persist(new Set()));
  }, [persist]);

  const isCollapsed = useCallback(
    (dateKey) => collapsed.has(dateKey), [collapsed]);

  return { isCollapsed, toggle, collapseAll, expandAll, collapsedCount: collapsed.size };
}
